import {Injectable} from '@angular/core';
import {BehaviorSubject, Observable} from 'rxjs';
import {take} from 'rxjs/operators';
import {UnrApiService} from '../../core/api/api.service';
import {KmOrMiles} from '../../core/enum/km-miles.enum';
import {Setting} from './setting';
import {SettingKeyEnum} from './setting-key.enum';

@Injectable(
  {providedIn: 'root'}
)
export class UnrSettingsService {

  static readonly DEFAULT_ACCURACY = 20;

  private kmOrMiles$: BehaviorSubject<KmOrMiles> = new BehaviorSubject<KmOrMiles>(KmOrMiles.KM);
  private coordinatesAccuracy$: BehaviorSubject<number> = new BehaviorSubject<number>(UnrSettingsService.DEFAULT_ACCURACY);
  private showWelcomeScreen: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);
  private settings: BehaviorSubject<Array<Setting>> = new BehaviorSubject<Array<Setting>>([]);

  constructor(private apiService: UnrApiService) {
  }

  setShowWelcomeScreen(showWelcomeScreen: any): void {
    this.showWelcomeScreen.next(String(showWelcomeScreen) === 'true');
  }

  getShowWelcomeScreen(): boolean {
    return this.showWelcomeScreen.getValue();
  }

  getShowWelcomeScreenAsObservable(): Observable<boolean> {
    return this.showWelcomeScreen.asObservable();
  }

  setKmOrMiles(kmOrMiles: KmOrMiles): void {
    this.kmOrMiles$.next(kmOrMiles);
  }

  getKmOrMiles(): Observable<KmOrMiles> {
    return this.kmOrMiles$.asObservable();
  }

  setCoordinatesAccuracy(accuracy: number) {
    this.coordinatesAccuracy$.next(accuracy);
  }

  getCoordinatesAccuracy(): Observable<number> {
    return this.coordinatesAccuracy$.asObservable();
  }

  getLatestCoordinatesAccuracy(): number {
    return this.coordinatesAccuracy$.getValue();
  }

  getCoordinatesAccuracyNeverLessThan5(): number {
    if (this.getLatestCoordinatesAccuracy() < 5) {
      return 5;
    } else {
      return this.getLatestCoordinatesAccuracy();
    }
  }

  // TODO: Combine saveSettings with saveAnySetting
  // https://gitlab.com/bart-blommaerts/unrestricted-app/-/issues/52#note_339423675
  saveSetting(settingsKey: SettingKeyEnum, value: any) {
    if (settingsKey === SettingKeyEnum.KM_OR_MILES) {
      this.apiService
        .saveSettings(this.createNewSettings(settingsKey, value))
        .pipe(
          take(1)
        )
        .subscribe();
    } else {
      this.apiService
        .saveAnySetting(this.createNewSettings(settingsKey, value))
        .pipe(
          take(1)
        )
        .subscribe();
    }
  }

  getAllSettings(): Observable<Array<Setting>> {
    return this.settings.asObservable();
  }

  setAllSettings(settings: Array<Setting>): Observable<Array<Setting>> {
    this.settings.next(settings);
    if (this.hasSetting(settings, SettingKeyEnum.SHOW_WELCOME_SCREEN)) {
      this.setShowWelcomeScreen(this.getSettingValue(settings, SettingKeyEnum.SHOW_WELCOME_SCREEN));
    }
    if (this.hasSetting(settings, SettingKeyEnum.RECORDING_ACCURACY)) {
      this.setCoordinatesAccuracy(this.getSettingValue(settings, SettingKeyEnum.RECORDING_ACCURACY, UnrSettingsService.DEFAULT_ACCURACY));
    }
    if (this.hasSetting(settings, SettingKeyEnum.KM_OR_MILES)) {
      this.setKmOrMiles(this.getSettingValue(settings, SettingKeyEnum.KM_OR_MILES, 'km'));
    }
    return this.getAllSettings();
  }

  private createNewSettings(settingsKey: SettingKeyEnum, value: any): Setting {
    switch (settingsKey) {
      case SettingKeyEnum.SHOW_WELCOME_SCREEN:
        this.setShowWelcomeScreen(value);
        return {key: SettingKeyEnum.SHOW_WELCOME_SCREEN, value};
      case SettingKeyEnum.KM_OR_MILES:
        this.setKmOrMiles(value ? KmOrMiles.MILES : KmOrMiles.KM);
        return {key: SettingKeyEnum.KM_OR_MILES, value: this.kmOrMiles$.getValue()};
      case SettingKeyEnum.RECORDING_ACCURACY:
        this.setCoordinatesAccuracy(value);
        return {key: SettingKeyEnum.RECORDING_ACCURACY, value: this.coordinatesAccuracy$.getValue()};
    }
  }

  private hasSetting(settings: Array<Setting>, key: SettingKeyEnum): boolean {
    return settings.find(setting => setting.key === key) !== undefined;
  }

  private getSettingValue(settings: Array<Setting>,
                          key: SettingKeyEnum,
                          defaultValue?: any): any {
    if (this.hasSetting(settings, key)) {
      return settings.find(setting => setting.key === key).value;
    } else {
      return defaultValue;
    }
  }
}
